import { useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { toast } from "sonner";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { deleteDiscount } from "../../../api/bourse_remise";
import { getStudentDiscountByYear } from "../../../api/studentPaiement";
import CreateBourseRemiseModal from "./createBourseRemiseModal";
import EditBourseRemiseModal from "./editBourseRemiseModal";
import FicheDiscountModalApercu from "./FicheDiscountModalApercu";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import withPermission from "../../../core/common/autorisations/withPermission";

type Props = {
  student?: any;
  currency?: string;
  onSaved?: () => void;
};

// boutton protégée pour créer une bourse ou remise
const ProtectedBtnAddBourse = withPermission(
  (props: any) => (
    <button
      className="btn btn-primary btn-sm"
      onClick={() => props.setShowCreate(true)}
    >
      Nouvelle bourse / remise
    </button>
  ),
  [buildPermission(Resource.BOURSE_REMISE, Action.CREATE)],
  "masquer",
);

// boutton protégée pour modifier une bourse ou remise
const ProtectedBtnUpdateBourse = withPermission(
  (props: any) => (
    <button
      className="btn btn-outline-warning btn-sm"
      onClick={() => {
        props.setSelectedDiscount(props.discount);
        props.setShowEdit(true);
      }}
    >
      <i className="ti ti-pencil" />
    </button>
  ),
  [buildPermission(Resource.BOURSE_REMISE, Action.UPDATE)],
  "masquer",
);

// boutton protégée pour supprimer une bourse ou remise
const ProtectedBtnDeleteBourse = withPermission(
  (props: any) => (
    <button
      className="btn btn-outline-danger btn-sm"
      onClick={() => props.showConfirmationAlert(props.discount)}
    >
      <i className="ti ti-trash" />
    </button>
  ),
  [buildPermission(Resource.BOURSE_REMISE, Action.DELETE)],
  "masquer",
);

// boutton protégée pour imprimer une bourse ou remise
const ProtectedBtnPrintBourse = withPermission(
  (props: any) => (
    <button
      className="btn btn-outline-primary btn-sm"
      onClick={() => {
        props.setSelectedDiscount(props.discount);
        props.setShowFicheDiscount(true);
      }}
    >
      <i className="ti ti-printer" />
    </button>
  ),
  [buildPermission(Resource.BOURSE_REMISE, Action.READ)],
  "masquer",
);

const StudentBourse = ({ student, onSaved, currency = "FCFA" }: Props) => {
  const { selectedYear } = useAcademicYear();
  const [isLoading, setIsLoading] = useState(false);
  const [discounts, setDiscounts] = useState<any[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showFicheDiscount, setShowFicheDiscount] = useState<boolean>(false);
  const [selectedDiscount, setSelectedDiscount] = useState<any>(null);

  const MySwal = withReactContent(Swal);

  const fetchDiscounts = async () => {
    if (!selectedYear?.id || !student?.id) {
      setDiscounts([]);
      return;
    }

    setIsLoading(true);
    try {
      const res = await getStudentDiscountByYear(
        student.id,
        selectedYear.id,
        buildPermissionContext(Resource.PAYMENT, Action.READ),
      );
      setDiscounts(
        (res?.data?.discount || []).map((discount: any) => ({
          ...discount,
          discount_id: discount.id,
          discount_amount: Number(discount.amount || 0),
          student_id: student.id,
        })),
      );
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erreur lors du chargement des bourses et remises",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscounts();
  }, [selectedYear?.id, student?.id]);

  const showConfirmationAlert = (discount: any) => {
    MySwal.fire({
      title: "Supprimer une Bourse / Remise ?",
      text: `Souhaitez-vous vraiment supprimer la bourse / remise ${discount?.title || ""} ?`,
      showCancelButton: true,
      confirmButtonText: "Envoyer",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      // ── Affiche le spinner pendant la requête ──
      MySwal.fire({
        title: "Suppression en cours...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => MySwal.showLoading(),
      });
      try {
        await deleteDiscount(discount?.discount_id);
        MySwal.fire({
          title: "Bourse / Remise supprimee avec succes !",
          icon: "success",
          confirmButtonText: "OK",
        });
        fetchDiscounts();
        if (onSaved) onSaved();
      } catch (error: any) {
        const err = error as AxiosError<any>;
        MySwal.fire({
          title: "Erreur",
          text:
            err.response?.data?.message ||
            err.response?.data?.error ||
            "Une erreur est survenue, reessayez plus tard",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    });
  };

  const total = useMemo(
    () =>
      discounts.reduce(
        (sum: number, item: any) => sum + Number(item?.discount_amount || 0),
        0,
      ),
    [discounts],
  );

  return (
    <>
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h6 className="mb-0">Bourses et Remises</h6>
          <ProtectedBtnAddBourse setShowCreate={setShowCreate} />
        </div>
        <div className="card-body p-0">
          {isLoading ? (
            <div className="p-3 text-center">Chargement...</div>
          ) : (
            <div
              className="table-responsive"
              style={{ maxHeight: 520, overflowY: "auto" }}
            >
              <table className="table mb-0">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Correspondant</th>
                    <th>Description</th>
                    <th className="text-end">Montant</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {discounts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        Aucune bourse ou remise trouvee pour cet etudiant.
                      </td>
                    </tr>
                  ) : (
                    discounts.map((discount: any) => (
                      <tr key={discount?.discount_id}>
                        <td>
                          <span
                            className={`badge ${
                              discount?.type === "Bourse"
                                ? "bg-success-subtle text-dark"
                                : "bg-primary-subtle text-dark"
                            }`}
                          >
                            {discount?.type === "Bourse" ? "Bourse" : "Remise"}
                          </span>
                        </td>
                        <td>{discount?.corresponding || "-"}</td>
                        <td>{discount?.description || "-"}</td>
                        <td className="text-end fw-semibold">
                          {Number(
                            discount?.discount_amount || 0,
                          ).toLocaleString("fr-FR")}{" "}
                          {currency}
                        </td>
                        <td className="text-end">
                          <div className="btn-group">
                            <ProtectedBtnPrintBourse
                              discount={discount}
                              setSelectedDiscount={setSelectedDiscount}
                              setShowFicheDiscount={setShowFicheDiscount}
                            />
                            <ProtectedBtnUpdateBourse
                              discount={discount}
                              setSelectedDiscount={setSelectedDiscount}
                              setShowEdit={setShowEdit}
                            />

                            <ProtectedBtnDeleteBourse
                              discount={discount}
                              showConfirmationAlert={showConfirmationAlert}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} className="fw-semibold">
                      Somme:
                    </td>
                    <td className="text-end fw-semibold text-primary">
                      {total.toLocaleString("fr-FR")} {currency}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      <CreateBourseRemiseModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        onSaved={() => {
          fetchDiscounts();
          if (onSaved) onSaved();
        }}
        initialStudentId={student?.id}
        lockStudent
        currency={currency}
      />

      <EditBourseRemiseModal
        show={showEdit}
        onClose={() => {
          setShowEdit(false);
          setSelectedDiscount(null);
        }}
        onSaved={() => {
          fetchDiscounts();
          if (onSaved) onSaved();
        }}
        record={selectedDiscount}
        lockStudent
        currency={currency}
      />
      <FicheDiscountModalApercu
        show={showFicheDiscount}
        onClose={() => setShowFicheDiscount(false)}
        metaData={selectedDiscount}
      />
    </>
  );
};

export default withPermission(
  StudentBourse,
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.PAYMENT, action),
    ),
  ],
  "masquer",
);
